import * as i0 from '@angular/core';
import { InjectionToken, Injectable, Inject, Pipe, Component, Input, APP_INITIALIZER, NgModule } from '@angular/core';
import { Observable } from 'rxjs';
import { getTranslateProps } from '@tolgee/web';
export * from '@tolgee/web';
const TOLGEE_INSTANCE = /*#__PURE__*/new InjectionToken('tolgee.instance');
let TranslateService = /*#__PURE__*/(() => {
  class TranslateService {
    _tolgee;
    _ngZone;
    runPromise;
    constructor(_tolgee, _ngZone) {
      this._tolgee = _tolgee;
      this._ngZone = _ngZone;
    }
    get tolgee() {
      return this._tolgee;
    }
    /**
     * Starts Tolgee if not started.
     *
     * The Tolgee is run outside NgZone. The triggered events, doesn't
     * trigger the change detection, when listening directly via Tolgee.on.
     *
     * However, all the emitting methods (on, translate, languageAsync)
     * in this class are emitting back in Angular's NgZone,
     * so the change detection works fine.
     */
    async start() {
      if (!this.isSSR()) {
        await this.runIfNotRunning();
      }
    }
    /**
     * Returns translation asynchronously, this method always return
     */
    translate = (...args) => {
      // @ts-ignore
      const params = getTranslateProps(...args);
      return new Observable(subscriber => {
        const loadPromise = this.tolgee.addActiveNs(params.ns);
        const translate = async () => {
          await loadPromise;
          const translated = this.tolgee.t(params);
          subscriber.next(translated);
        };
        // noinspection JSIgnoredPromiseFromCall
        translate();
        const subscription = this.tolgee.on('update', translate);
        return () => {
          this.tolgee.removeActiveNs(params.ns);
          subscription.unsubscribe();
        };
      });
    };
    /**
     * Instantly returns translated value. May return undefined or outdated value.
     * Use only when you cannot use translate.
     */
    instant = (...args) => {
      // @ts-ignore
      const params = getTranslateProps(...args);
      return this.tolgee.t(params);
    };
    /**
     * Returns current language
     */
    get language() {
      return this.tolgee.getLanguage();
    }
    /**
     * Returns an observable emitting current language.
     * It instantly emits current language
     */
    get languageAsync() {
      return new Observable(subscriber => {
        subscriber.next(this.tolgee.getLanguage());
        const subscription = this.on('language').subscribe(value => {
          subscriber.next(value.value);
        });
        return () => subscription.unsubscribe();
      });
    }
    /**
     * Changes the current language
     * @param lang The new current language (e.g. en, en-US)
     * @return Promise<void> Resolves when translations
     * for given language are loaded
     */
    changeLanguage(lang) {
      return this.tolgee.changeLanguage(lang);
    }
    /**
     * Returns observable emitting when specified event is triggered.
     *
     * It's a wrapper of Tolgee.on method. The emmit is triggered inside ngZone,
     * so change detection should work properly.
     *
     * @param event the event to listen
     */
    on(event) {
      return new Observable(subscriber => {
        const subscription = this.tolgee.on(event, value => {
          this._ngZone.run(() => {
            subscriber.next(value);
          });
        });
        return () => subscription.unsubscribe();
      });
    }
    ngOnDestroy() {
      this.tolgee.stop();
    }
    async runIfNotRunning() {
      if (!this.runPromise) {
        this.runPromise = this._ngZone.runOutsideAngular(() => {
          return this.tolgee.run();
        });
      }
      await this.runPromise;
    }
    isSSR() {
      return typeof window === 'undefined';
    }
    static ɵfac = function TranslateService_Factory(t) {
      return new (t || TranslateService)(i0.ɵɵinject(TOLGEE_INSTANCE), i0.ɵɵinject(i0.NgZone));
    };
    static ɵprov = /*@__PURE__*/i0.ɵɵdefineInjectable({
      token: TranslateService,
      factory: TranslateService.ɵfac,
      providedIn: 'root'
    });
  }
  return TranslateService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let TranslatePipe = /*#__PURE__*/(() => {
  class TranslatePipe {
    translateService;
    _cdr;
    value = '';
    previousHash;
    subscription;
    constructor(translateService, _cdr) {
      this.translateService = translateService;
      this._cdr = _cdr;
    }
    ngOnDestroy() {
      this.unsubscribe();
    }
    transform = (...args) => {
      // @ts-ignore
      const params = getTranslateProps(...args);
      const {
        key
      } = params;
      if (!key || key.length === 0) {
        return key;
      }
      const newHash = this.hash(params);
      if (this.previousHash === newHash) {
        return this.value;
      }
      this.previousHash = newHash;
      this.translate(params);
      return this.value;
    };
    hash(props) {
      return JSON.stringify([props, this.translateService.tolgee.getLanguage()]);
    }
    unsubscribe() {
      this.subscription?.unsubscribe();
      this.subscription = null;
    }
    translate(props) {
      this.value = this.translateService.instant({
        ...props,
        orEmpty: true
      });
      this.subscribe(props);
    }
    subscribe(props) {
      this.unsubscribe();
      this.subscription = this.translateService.translate(props).subscribe(r => {
        this.value = r;
        this._cdr.detectChanges();
      });
    }
    static ɵfac = function TranslatePipe_Factory(t) {
      return new (t || TranslatePipe)(i0.ɵɵdirectiveInject(TranslateService, 16), i0.ɵɵdirectiveInject(i0.ChangeDetectorRef, 16));
    };
    static ɵpipe = /*@__PURE__*/i0.ɵɵdefinePipe({
      name: "translate",
      type: TranslatePipe,
      pure: false
    });
  }
  return TranslatePipe;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
const _c0 = ["t", ""];
let TComponent = /*#__PURE__*/(() => {
  class TComponent {
    ref;
    translateService;
    key;
    ns;
    params;
    default;
    noWrap = false;
    language;
    /**
     * When true, innerHTML property of element is set.
     * Use only when you're sure the HTML is sanitized.
     */
    isHtml = false;
    subscription;
    initialized;
    constructor(ref, translateService) {
      this.ref = ref;
      this.translateService = translateService;
    }
    ngOnInit() {
      // noinspection JSIgnoredPromiseFromCall
      this.subscribe();
      this.renderInstantValue();
    }
    ngOnChanges() {
      // noinspection JSIgnoredPromiseFromCall
      this.subscribe();
    }
    ngOnDestroy() {
      this.unsubscribe();
    }
    renderInstantValue() {
      // get initial value first
      const translated = this.translateService.instant({
        ...this.getTranslateProps(),
        orEmpty: true
      });
      this.setElementContent(translated);
    }
    getTranslateProps() {
      return {
        key: this.key,
        ns: this.ns,
        params: this.params,
        defaultValue: this.default,
        noWrap: this.noWrap,
        language: this.language
      };
    }
    unsubscribe() {
      this.subscription?.unsubscribe();
    }
    async subscribe() {
      this.unsubscribe();
      this.subscription = this.translateService.translate(this.getTranslateProps()).subscribe(translated => {
        this.setElementContent(translated);
        this.initialized = true;
      });
    }
    setElementContent(translated) {
      if (this.isHtml) {
        this.getElement().innerHTML = translated;
        return;
      }
      this.getElement().textContent = translated;
    }
    getElement() {
      return this.ref.nativeElement;
    }
    static ɵfac = function TComponent_Factory(t) {
      return new (t || TComponent)(i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(TranslateService));
    };
    static ɵcmp = /*@__PURE__*/i0.ɵɵdefineComponent({
      type: TComponent,
      selectors: [["", "t", ""]],
      inputs: {
        key: "key",
        ns: "ns",
        params: "params",
        default: "default",
        noWrap: "noWrap",
        language: "language",
        isHtml: "isHtml"
      },
      features: [i0.ɵɵNgOnChangesFeature],
      attrs: _c0,
      decls: 0,
      vars: 0,
      template: function TComponent_Template(rf, ctx) {},
      encapsulation: 2
    });
  }
  return TComponent;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let LoaderComponent = /*#__PURE__*/(() => {
  class LoaderComponent {
    html;
    static ɵfac = function LoaderComponent_Factory(t) {
      return new (t || LoaderComponent)();
    };
    static ɵcmp = /*@__PURE__*/i0.ɵɵdefineComponent({
      type: LoaderComponent,
      selectors: [["ng-component"]],
      inputs: {
        html: "html"
      },
      decls: 1,
      vars: 1,
      consts: [[1, "loader-template", 3, "innerHTML"]],
      template: function LoaderComponent_Template(rf, ctx) {
        if (rf & 1) {
          i0.ɵɵelement(0, "div", 0);
        }
        if (rf & 2) {
          i0.ɵɵproperty("innerHTML", ctx.html, i0.ɵɵsanitizeHtml);
        }
      },
      encapsulation: 2
    });
  }
  return LoaderComponent;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let NgxTolgeeModule = /*#__PURE__*/(() => {
  class NgxTolgeeModule {
    static ɵfac = function NgxTolgeeModule_Factory(t) {
      return new (t || NgxTolgeeModule)();
    };
    static ɵmod = /*@__PURE__*/i0.ɵɵdefineNgModule({
      type: NgxTolgeeModule
    });
    static ɵinj = /*@__PURE__*/i0.ɵɵdefineInjector({
      providers: [{
        provide: APP_INITIALIZER,
        useFactory: service => {
          return async () => await service.start();
        },
        deps: [TranslateService],
        multi: true
      }]
    });
  }
  return NgxTolgeeModule;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
(function () {
  (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(NgxTolgeeModule, {
    declarations: [TranslatePipe, TComponent, LoaderComponent],
    exports: [TranslatePipe, TComponent]
  });
})();
let NamespaceResolver = /*#__PURE__*/(() => {
  class NamespaceResolver {
    service;
    constructor(service) {
      this.service = service;
    }
    async resolve(route) {
      const ns = this.getNamespace(route);
      await this.service.tolgee.addActiveNs(ns, true);
    }
    getNamespace(route) {
      const namespace = route?.data?.tolgeeNamespace;
      if (namespace === undefined) {
        console.warn('No namespace provided. Please add tolgeeNamespace to your route data. \n' + 'If you really want to lazy load default namespace set tolgeeNamespace to empty string');
      }
      return namespace;
    }
    static ɵfac = function NamespaceResolver_Factory(t) {
      return new (t || NamespaceResolver)(i0.ɵɵinject(TranslateService));
    };
    static ɵprov = /*@__PURE__*/i0.ɵɵdefineInjectable({
      token: NamespaceResolver,
      factory: NamespaceResolver.ɵfac,
      providedIn: 'root'
    });
  }
  return NamespaceResolver;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/*
 * Public API Surface of ngx-tolgee
 */

/**
 * Generated bundle index. Do not edit.
 */

export { NamespaceResolver, NgxTolgeeModule, TComponent, TOLGEE_INSTANCE, TranslatePipe, TranslateService };
